import { ReactElement, ReactNode } from 'react';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { PortalDragOverlay } from '@components/drag-and-drop';
import { OverlayContainer, useSortingDefaults } from '@components/drag-and-drop/defaults';
import { IconProps } from '@components/icons';
import { IconDropdown } from '@components/icons/dropdown';
import { IconWorkspace } from '@components/icons/workspace';
import { Nav } from '@components/nav';
import {
  NavExpandableSectionProps,
  NavGroupType,
  NavItemLinkProps,
  NavLinkPlaceholderContainer,
  ShownOnOpen,
} from '@components/nav/nav';
import { Stack } from '@components/stack';
import { SelectionStore } from '@stores/create-selection-store';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type Link = {
  id: string;
  href: string;
  icon: ReactElement<IconProps>;
  label: string;
  iconRight?: ReactElement<IconProps>;
};

const withSortable = <T extends object>(
  BaseComponent: React.ComponentType<T>,
  SortableComponent: React.ComponentType<T>
) => {
  return (props: T) => {
    const Component = props.hasOwnProperty('onDragEnd') ? SortableComponent : BaseComponent;
    return <Component {...props} />;
  };
};

export type NavGroupCollapseProps = {
  id: string;
  label: string;
  links: Link[];
  toggleGroup: (id: string) => void;
  expandedSelectionStore: SelectionStore;
  onDragEnd?: (newOrder: Array<string>) => void;
};

export const NavGroupCollapse = ({
  toggleGroup,
  label,
  id,
  expandedSelectionStore,
  ...rest
}: NavGroupCollapseProps) => {
  const isCollapsed = expandedSelectionStore.useStore((state) => state[id]);

  return (
    <ShownOnOpen>
      <GroupContainer onClick={() => toggleGroup(id)}>
        <Nav.GroupHeader text={label} />
        <IconContainer className={!isCollapsed ? 'arrow down' : 'arrow right'}>
          <IconDropdown color="purple50" />
        </IconContainer>
      </GroupContainer>
      <Collapse isCollapsed={isCollapsed} overflowType="visible">
        <Links id={id} {...rest} />
      </Collapse>
    </ShownOnOpen>
  );
};

type LinksProps = Pick<NavGroupCollapseProps, 'id' | 'links' | 'onDragEnd'> & {
  isSortable?: boolean;
};

const BaseLinks = ({ links, isSortable, ...rest }: LinksProps) => {
  const Link = isSortable ? SortableLink : Nav.Link;
  const filteredLinks = links.filter((link) => link.href && link.icon && link.label);

  return (
    <Nav.Group>
      {filteredLinks.map(({ icon, id, href, ...linkRest }) => {
        return (
          <Link key={id || href} iconLeft={icon} href={href} {...rest} {...linkRest} id={id} />
        );
      })}
    </Nav.Group>
  );
};

const SortableLink = (props: NavItemLinkProps & BaseItem) => {
  return <SortableItem component={Nav.Link} {...props} />;
};

const SortableLinks = ({ onDragEnd, ...rest }: LinksProps) => {
  return (
    <SortableItems
      onDragEnd={onDragEnd}
      items={rest.links}
      renderPlaceholder={(link) => {
        const { icon, ...linkRest } = link as Link;
        return (
          <NavLinkPlaceholderContainer>
            <Nav.Link iconLeft={icon} {...linkRest} />
          </NavLinkPlaceholderContainer>
        );
      }}
      renderBase={() => <BaseLinks onDragEnd={onDragEnd} {...rest} isSortable />}
    />
  );
};

const Links = withSortable(BaseLinks, SortableLinks);

type BaseItem = {
  id: string;
};

type SortableItemsProps<T extends BaseItem> = {
  items: Array<T>;
  renderPlaceholder: (item: T) => ReactNode;
  onDragEnd?: (newOrder: Array<string>) => void;
  renderBase: () => ReactNode;
};

const SortableItems = <T extends BaseItem>({
  items,
  onDragEnd,
  renderPlaceholder,
  renderBase,
}: SortableItemsProps<T>) => {
  const {
    resetState,
    activeId,
    overId,
    offset,
    setOverId,
    handleDragStart,
    handleDragOver,
    strategy,
    ...sortingDefaults
  } = useSortingDefaults();

  const itemIds = items.map((item) => item.id);
  const activeItem = items.find((item) => item.id === activeId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeIndex = itemIds.findIndex((id) => id === active.id);
    const overIndex = itemIds.findIndex((id) => id === over?.id);
    if (activeIndex > -1 && overIndex > -1) {
      const newOrder = arrayMove(itemIds, activeIndex, overIndex);
      onDragEnd?.(newOrder);
    }
  };

  return (
    <DndContext
      {...sortingDefaults}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
    >
      <SortableContext items={itemIds} strategy={strategy}>
        {renderBase()}
      </SortableContext>

      <PortalDragOverlay dropAnimation={null}>
        {activeItem && <OverlayContainer>{renderPlaceholder(activeItem)}</OverlayContainer>}
      </PortalDragOverlay>
    </DndContext>
  );
};

type NavGroupOfGroupsCollapseProps = {
  id: string;
  label: string;
  groups: Array<NavGroupType>;
  addSidebar?: any;
  toggleGroup: (id: string) => void;
  onDragEnd?: (newOrder: Array<string>) => void;
  expandedSelectionStore: SelectionStore;
};

export const NavGroupOfGroupsCollapse = ({
  label,
  id,
  toggleGroup,
  expandedSelectionStore,
  addSidebar,
  ...rest
}: NavGroupOfGroupsCollapseProps) => {
  const state = expandedSelectionStore.useStore();
  const getIsCollapsed = (id: string) => state[id];
  const isCollapsed = getIsCollapsed(id);
  return (
    <>
      <Nav.GroupItemHeader
        iconLeft={<IconWorkspace size={16} color="buttonIconPositive" />}
        label={label}
        id={id}
        toggleGroup={toggleGroup}
        isCollapsed={getIsCollapsed}
        addSidebar={addSidebar}
      />
      <Collapse isCollapsed={isCollapsed} overflowType="visible">
        <NavGroups id={id} toggleGroup={toggleGroup} isCollapsed={getIsCollapsed} {...rest} />
      </Collapse>
    </>
  );
};

type SortableItemProps = {
  id: string;
  component: React.ComponentType<any>;
};

const SortableItem = ({ id, component: Component, ...rest }: SortableItemProps) => {
  const { attributes, listeners, setDraggableNodeRef, setDroppableNodeRef, transform, isDragging } =
    useSortable({
      id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    ...(isDragging && {
      opacity: 0,
    }),
  };

  return (
    <div ref={setDroppableNodeRef}>
      <div style={style} ref={setDraggableNodeRef}>
        <Component {...rest} {...attributes} {...listeners} />
      </div>
    </div>
  );
};

type NavGroupsProps = Pick<
  NavGroupOfGroupsCollapseProps,
  'groups' | 'onDragEnd' | 'id' | 'toggleGroup'
> & { isCollapsed: (id: string) => boolean };

type BaseNavGroupsProps = NavGroupsProps & {
  isSortable?: boolean;
};

const BaseNavGroups = ({ groups, id, isSortable = false, ...rest }: BaseNavGroupsProps) => {
  const ExpandableSectionHeader = isSortable
    ? SortableExpandableSectionHeader
    : Nav.ExpandableSectionHeader;

  return (
    <>
      {groups.map(({ id: groupId, ...groupRest }) => (
        <ExpandableSectionHeader
          key={groupId}
          groupId={groupId}
          collapseId={id}
          data-testid={groupRest.label}
          {...groupRest}
          {...rest}
        />
      ))}
    </>
  );
};

const SortableExpandableSectionHeader = (props: NavExpandableSectionProps) => {
  return <SortableItem id={props.groupId} component={Nav.ExpandableSectionHeader} {...props} />;
};

const SortableNavGroups = ({ onDragEnd, ...rest }: NavGroupsProps) => {
  return (
    <SortableItems
      onDragEnd={onDragEnd}
      items={rest.groups}
      renderPlaceholder={(group) => {
        return (
          <Nav.SectionPlaceholder
            group={group}
            isCollapsed={rest.isCollapsed(`${rest.id}-${group.id}`)}
          />
        );
      }}
      renderBase={() => <BaseNavGroups onDragEnd={onDragEnd} {...rest} isSortable />}
    />
  );
};

const NavGroups = withSortable(BaseNavGroups, SortableNavGroups);

const GroupContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
  align: 'center',
})({
  cursor: 'pointer',
  height: pxToRem(32),
  borderRadius: pxToRem(8),
  '&:hover': {
    background: theme.color.whiteTrans5,
  },
});

const IconContainer = styled.span({
  display: 'flex',
  transition: 'transform 150ms ease-out',
  padding: `${pxToRem(0)} ${pxToRem(8)}`,
  '&.right': {
    transform: 'rotate(-90deg)',
  },
});
